import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  Button,
  OverlayTrigger,
  Popover,
  Tooltip,
  Accordion
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { ScrollView } from "devextreme-react";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GeneralUtils from "GeneralUtils";
import { IUserDetails } from "interfaces";
import { httpIAM } from "services/http.IAM";
import userDataUtil from "services/user-data-util";
import logo from "../../../assets/icons/logo.svg";
import avatar from "../../../assets/images/avatar.svg";
import Avatar from "../../template-component/avatar/Avatar";
import SidebarNavItem from "./sidebar-nav-item/SidebarNavItem";
import AppConstants from "../../../constants.js";

import "./sidebar-nav.scss";

import routes from "../../../routes";
import runtimeEnv, { isSidebarFeatureEnabled } from "../../../runtime-env";
import {
  getDatabaseDisplayFromUser,
  getDisplayFromUserDetails
} from "./sidebar-nav-database-utils";

function getDatabaseDisplayFromCookie(): string {
  const userDetail = GeneralUtils.getCookieValue("USER_DETAILS");
  const databaseId = GeneralUtils.getCookieValue("DATABASE");
  return getDisplayFromUserDetails(userDetail, databaseId);
}

const SidebarNav = (props: any) => {
  const [reloading, setReloading] = useState(true);
  const [databaseDisplay, setDatabaseDisplay] = useState<string>("—");

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const fromCookie = getDatabaseDisplayFromCookie();
    if (fromCookie !== "—") {
      setDatabaseDisplay(fromCookie);
      setReloading(false);
      return;
    }
    const authToken = GeneralUtils.getCookieValue("AUTH_TOKEN");
    if (!authToken) {
      setDatabaseDisplay("—");
      setReloading(false);
      return;
    }
    try {
      const token = jwtDecode(authToken) as { UserName?: string };
      const userName = token?.UserName;
      if (!userName) {
        setDatabaseDisplay("—");
        setReloading(false);
        return;
      }
      GeneralUtils.appendCookie("INIT_USER_STATS", "");
      GeneralUtils.appendCookie("AUTH_TOKEN", authToken);
      httpIAM
        .getUserDetails(userName)
        .then((user: IUserDetails) => {
          GeneralUtils.appendCookie("USER_DETAILS", JSON.stringify(user));
          const previousDatabase = GeneralUtils.getCookieValue("DATABASE");
          if (
            user?.databases?.length > 0 &&
            (previousDatabase === "" ||
              user.databases.find((x) => x.id === previousDatabase) ===
                undefined)
          ) {
            GeneralUtils.appendCookie("DATABASE", user.databases[0].id);
          }
          userDataUtil.initializeUserStats();
          const display = getDatabaseDisplayFromUser(
            user,
            GeneralUtils.getCookieValue("DATABASE") || null
          );
          setDatabaseDisplay(display);
          setReloading((prev) => !prev);
        })
        .catch(() => {
          setDatabaseDisplay("—");
          setReloading(false);
        });
    } catch {
      setDatabaseDisplay("—");
      setReloading(false);
    }
  }, []);

  const sidebarNavItems = () =>
    routes
      .getRoutes()
      ?.filter(
        (item) =>
          item.visibleInSidebar && isSidebarFeatureEnabled(item.flagKey)
      )
      ?.map((item) => {
        const visibleSubRoutes = item.subRoutes?.filter(
          (sub) =>
            sub.visibleInSidebar && isSidebarFeatureEnabled(sub.flagKey)
        );
        const displayTitle = item.title?.includes("Database")
          ? `Database\n(${databaseDisplay})`
          : item.title;
        return (
          <React.Fragment key={displayTitle}>
            {item.subRoutes ? (
              <Accordion defaultActiveKey="0">
                <Accordion.Toggle
                  as={Button}
                  variant=""
                  eventKey="0"
                  className="p0 ml0 w-100"
                >
                  <OverlayTrigger
                    key={`sidebar_overlay_${displayTitle}`}
                    placement="right"
                    overlay={
                      <Tooltip
                        id={`tooltip_${displayTitle}`}
                        className={
                          (showMenuLabel || "").toString() === "true"
                            ? "d-none"
                            : "d-block"
                        }
                        style={{ marginLeft: "-170px" }}
                      >
                        <span className="color-light-hover">
                          <span className="color-light color-light-hover">
                            {displayTitle}
                          </span>
                        </span>
                      </Tooltip>
                    }
                  >
                    <SidebarNavItem
                      // className={props.showMenuLabel ? '' : 'd-none'}
                      keys={`navitem_${displayTitle}`}
                      selected={false}
                      icon={item.icon}
                      title={displayTitle}
                      showMenuLabel={props.showMenuLabel}
                      showExpandIcon
                    />
                  </OverlayTrigger>
                </Accordion.Toggle>
                <Accordion.Collapse eventKey="0">
                  <>
                    {(visibleSubRoutes ?? []).map((subItem) => (
                      <OverlayTrigger
                        key={`sidebar_overlay_${subItem.title}`}
                        placement="right"
                        overlay={
                          <Tooltip
                            id={`tooltip_${subItem.title}`}
                            className={
                              (showMenuLabel || "").toString() === "true"
                                ? "d-none"
                                : "d-block"
                            }
                            style={{ marginLeft: "-170px" }}
                          >
                            <span className="color-light-hover">
                              <span className="color-light color-light-hover">
                                {subItem.title}
                              </span>
                            </span>
                          </Tooltip>
                        }
                      >
                        <Link
                          to={subItem.path}
                          key={`sub_link_${subItem.title}`}
                        >
                          <SidebarNavItem
                            className={
                              props.showMenuLabel ? "pl70 ml1 mt-1" : ""
                            }
                            keys={`navitem_${subItem.title}`}
                            selected={subItem.path === location.pathname}
                            icon={subItem.icon}
                            title={subItem.title}
                            showMenuLabel={props.showMenuLabel}
                            isChild
                          />
                        </Link>
                      </OverlayTrigger>
                    ))}
                  </>
                </Accordion.Collapse>
              </Accordion>
            ) : (
              <OverlayTrigger
                key={`sidebar_overlay_${displayTitle}`}
                placement="right"
                overlay={
                  <Tooltip
                    id={`tooltip_${displayTitle}`}
                    className={
                      (showMenuLabel || "").toString() === "true"
                        ? "d-none"
                        : "d-block"
                    }
                    style={{ marginLeft: "-170px" }}
                  >
                    <span className="color-light-hover">
                      <span className="color-light color-light-hover">
                        {displayTitle}
                      </span>
                    </span>
                  </Tooltip>
                }
              >
                <Link to={item.path} key={`link_${displayTitle}`}>
                  <SidebarNavItem
                    keys={`navitem_${displayTitle}`}
                    selected={item.path === location.pathname}
                    icon={item.icon}
                    title={displayTitle}
                    showMenuLabel={props.showMenuLabel}
                  />
                </Link>
              </OverlayTrigger>
            )}
          </React.Fragment>
        );
      });

  const quickLinks = routes.getQuickLinks().map((item) => (
    <div key={item.path} className="sidebar-quicklinks">
      {item.title}
    </div>
  ));

  const { showMenuLabel, collapseSidebar } = props;

  const accountOptions = (
    <Popover id="popover-basic">
      <Popover.Content className="w100 p0">
        <Button
          disabled
          variant="link"
          className="w-100 d-block p3 break-bottom"
        >
          Options
        </Button>
        <Button
          disabled
          variant="link"
          className="w-100 d-block p3 break-bottom"
        >
          Settings
        </Button>
        <Button
          variant="link"
          onClick={() => {
            localStorage.clear();
            history.push("/");
            const selectedDatabase = GeneralUtils.getCookieValue("DATABASE");
            GeneralUtils.clearSessionCookiesAndGlobalVars();
            GeneralUtils.removeAllCookies();
            GeneralUtils.appendCookie("DATABASE", selectedDatabase);
          }}
          className="w-100 d-block p3"
        >
          Logout
        </Button>
      </Popover.Content>
    </Popover>
  );

  let userDetails = {
    tenantId: "",
    userId: "",
    firstName: "",
    lastName: "",
    username: ""
  };
  const userDetailsCookie = GeneralUtils.getCookieValue("USER_DETAILS");
  if (userDetailsCookie && typeof userDetailsCookie === "string") {
    try {
      userDetails = JSON.parse(userDetailsCookie);
    } catch {
      // keep defaults on malformed cookie
    }
  }

  return (
    <div className="sidebar">
      <div
        style={{
          backgroundColor: "transparent",
          border: "none"
        }}
        className="logo sidebar-header"
      >
        <img src={logo} alt="Logo" />
        <span
          className={
            showMenuLabel === false ? "fade-to-none" : "fade-to-show"
          }
        >
          yieldWerx
        </span>
      </div>
      <div className="sidebar-scroll">
        <ScrollView showScrollbar="onScroll" scrollByThumb reachBottomText="">
          <div className="sidebar-menu">{sidebarNavItems()}</div>
        </ScrollView>
      </div>
      <div
        className={`sidebar-bottom ${
          showMenuLabel === false ? "fade-to-none" : "fade-to-show"
        }`}
      >
        {!AppConstants.isTempHidden && (
          <>
            <div className="sidebar-section-title">Quick Links</div>
            {quickLinks}
          </>
        )}

        <div className="avatar-container ml-20 pl20 pt20 pb20 d-flex align-items-center justify-content-between">
          <Avatar
            size="lg"
            mode="light"
            img={avatar}
            title={GeneralUtils.toTitleCase(
              `${userDetails.firstName} ${userDetails.lastName}`,
              false
            )}
            subtitle={`@${userDetails.username}`}
          />

          <OverlayTrigger
            rootClose
            trigger="click"
            placement="top"
            overlay={accountOptions}
          >
            <FontAwesomeIcon
              className="color-light cursor-pointer"
              icon={faChevronDown}
            />
          </OverlayTrigger>
        </div>

        <div className="sidebar-copyright-text">
          <span className="company color-light d-block">
            © Copyright {new Date().getFullYear()} yieldWerx.
            <br />
            All Rights Reserved.
          </span>
          <span className="company color-light d-block">
            {runtimeEnv.ENV}
            {runtimeEnv.BUILD_VERSION && `: ${runtimeEnv.BUILD_VERSION}`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SidebarNav;
